import type RootStore from "stores/RootStore";
import type { RouteType } from "types/RouteType";
import type RecurringStore from "./RecurringStore";

/**
 * Billing → Recurring. Gated by gateBillingRoutes, like every billing route,
 * and `requiresAdmin` on top: these schedules mint company invoices on their
 * own, so they match the Invoices posture rather than the read-only pages'.
 * The API's finance-role RBAC remains the authority.
 */
const recurringRoute: RouteType[] = [
  {
    name: "Recurring Invoices",
    path: "/billing/recurring",
    layout: "/app",
    viewportCard: {
      header: {
        title: "Recurring Invoices",
        component: () => import("./Recurring.Header"),
        enableRefreshButton: true,
        enableBackButton: true,
      },
      body: { component: () => import("./Recurring.Body") },
    },
    group: "Billing",
    hidden: false,
    requiresAuth: true,
    requiresAdmin: true,
    requiresPermission: false,
    enableAutoFind: false,
    channels: ["recurring-invoices"],
    onEnter: async ({ rootStore }) => {
      const store = (rootStore as unknown as { recurringStore?: RecurringStore }).recurringStore;
      void store?.initialize();
    },
    dialogs: [
      {
        // Item 50 · This page registered NO dialog, so there was no way to
        // create a schedule from it on any device — the empty state was a dead
        // end on a page whose whole subject is schedules.
        name: "RecurringFormDialog",
        title: "New Recurring Schedule",
        component: () => import("./dialogs/RecurringForm.Body"),
        cancelButton: true,
        height: 640,
        submitButtonText: "Create schedule",
        // A handler that discarded `save()`'s boolean threw away the API's
        // error AND the user's typed form — a deleted client tripping
        // `requireLiveClient` looked like the create had silently worked.
        // Throwing keeps the dialog open with the reason in its footer;
        // resolving closes it.
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const store = (rootStore as unknown as { recurringStore?: RecurringStore })
            .recurringStore;
          if (!(await store?.save())) {
            throw new Error(store?.formError || "Failed to create the schedule.");
          }
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = (rootStore as unknown as { recurringStore?: RecurringStore })
            .recurringStore;
          return store?.isFormValid === true;
        },
      },
    ],
  },
];

export default recurringRoute;
